import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { genKey } from 'draft-js';

import Entry from './Entry/Entry';
import addMention from '../modifiers/addMention';
import getSearchText from '../utils/getSearchText';
import defaultEntryComponent from './Entry/DefaultEntryComponent';
import defaultPositionSuggestions from '../utils/positionSuggestions';

import getTriggerForMention from '../utils/getTriggerForMention';
import Popover from './Popover';

import { isUndefined, first, has, groupBy, forEach } from 'lodash';
export class MentionSuggestions extends Component {
  static propTypes = {
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    entityMutability: PropTypes.oneOf(['SEGMENTED', 'IMMUTABLE', 'MUTABLE']),
    entryComponent: PropTypes.func,
    onAddMention: PropTypes.func,
    suggestions: PropTypes.array.isRequired,
  };

  state = {
    focusedOptionIndex: 0,
  };

  key = genKey();
  popover;
  activeOffsetKey;
  lastSearchValue;
  lastActiveTrigger = '';
  lastSelectionIsInsideWord;

  constructor(props) {
    super(props);
    this.props.callbacks.onChange = this.onEditorStateChange;
  }

  componentDidUpdate() {
    if (this.popover) {
      // In case the list shrinks there should be still an option focused.
      // Note: this might run multiple times and deduct 1 until the condition is
      // not fullfilled anymore.
      const size = this.props.suggestions.length;
      if (size > 0 && this.state.focusedOptionIndex >= size) {
        this.setState({
          focusedOptionIndex: size - 1,
        });
      }

      // Note: this is a simple protection for the error when componentDidUpdate
      // try to get new getPortalClientRect, but the key already was deleted by
      // previous action. (right now, it only can happened when set the mention
      // trigger to be multi-characters which not supported anyway!)
      if (!this.props.store.getAllSearches().has(this.activeOffsetKey)) {
        return;
      }

      const decoratorRect = this.props.store.getPortalClientRect(this.activeOffsetKey);
      const positionSuggestions = this.props.positionSuggestions || defaultPositionSuggestions;
      const newStyles = positionSuggestions({
        decoratorRect,
        props: this.props,
        popover: this.popover,
      });
      for (const [key, value] of Object.entries(newStyles)) {
        // eslint-disable-next-line
        this.popover.style[key] = value;
      }
    }
  }

  componentWillUnmount() {
    this.props.callbacks.onChange = undefined;
  }

  onEditorStateChange = (editorState) => {
    const searches = this.props.store.getAllSearches();
    // if no search portal is active there is no need to show the popover
    if (searches.size === 0) {
      return editorState;
    }

    const removeList = () => {
      this.props.store.resetEscapedSearch();
      this.closeDropdown();
      return editorState;
    };

    const triggerForMention = getTriggerForMention(
      editorState,
      searches,
      this.props.mentionTriggers,
    );

    if (!triggerForMention) {
      return removeList();
    }

    const lastActiveOffsetKey = this.activeOffsetKey;
    this.activeOffsetKey = triggerForMention.activeOffsetKey;

    this.onSearchChange(
      editorState,
      editorState.getSelection(),
      this.activeOffsetKey,
      lastActiveOffsetKey,
      triggerForMention.activeTrigger,
    );

    // make sure the escaped search is reseted in the cursor since the user
    // already switched to another mention search
    if (!this.props.store.isEscaped(this.activeOffsetKey || '')) {
      this.props.store.resetEscapedSearch();
    }

    // If none of the above triggered to close the window, it's safe to assume
    // the dropdown should be open. This is useful when a user focuses on another
    // input field and then comes back: the dropdown will show again.
    if (!this.props.open && !this.props.store.isEscaped(this.activeOffsetKey || '')) {
      this.openDropdown();
    }

    // makes sure the focused index is reseted every time a new selection opens
    // or the selection was moved to another mention search
    if (lastActiveOffsetKey !== this.activeOffsetKey) {
      this.setState({
        focusedOptionIndex: 0,
      });
    }

    return editorState;
  };

  onSearchChange = (editorState, selection, activeOffsetKey, lastActiveOffsetKey, trigger) => {
    const { matchingString: searchValue } = getSearchText(editorState, selection, [trigger]);

    if (
      this.lastActiveTrigger !== trigger ||
      this.lastSearchValue !== searchValue ||
      activeOffsetKey !== lastActiveOffsetKey
    ) {
      this.lastActiveTrigger = trigger;
      this.lastSearchValue = searchValue;
      this.props.onSearchChange({ trigger, value: searchValue });
    }
  };

  onDownArrow = (keyboardEvent) => {
    keyboardEvent.preventDefault();
    const newIndex = this.state.focusedOptionIndex + 1;
    this.onMentionFocus(newIndex >= this.props.suggestions.length ? 0 : newIndex);
  };

  onTab = (keyboardEvent) => {
    keyboardEvent.preventDefault();
    this.commitSelection();
  };

  onUpArrow = (keyboardEvent) => {
    keyboardEvent.preventDefault();
    if (this.props.suggestions.length > 0) {
      const newIndex = this.state.focusedOptionIndex - 1;
      this.onMentionFocus(newIndex < 0 ? this.props.suggestions.length - 1 : newIndex);
    }
  };

  onEscape = (keyboardEvent) => {
    keyboardEvent.preventDefault();

    this.props.store.escapeSearch(this.activeOffsetKey || '');
    this.closeDropdown();

    // to force a re-render of the outer component to change the aria props
    this.props.store.setEditorState(this.props.store.getEditorState());
  };

  onMentionSelect = (mention) => {
    // Note: This can happen in case a user typed @xxx (invalid mention) and
    // then hit Enter. Then the mention will be undefined.
    if (!mention) {
      return;
    }

    if (this.props.onAddMention) {
      this.props.onAddMention(mention);
    }

    this.closeDropdown();
    const newEditorState = addMention(
      this.props.store.getEditorState(),
      mention,
      this.props.mentionPrefix,
      this.lastActiveTrigger || '',
      this.props.entityMutability,
    );
    this.props.store.setEditorState(newEditorState);
  };

  onMentionFocus = (index) => {
    const descendant = `mention-option-${this.key}-${index}`;
    this.props.ariaProps.ariaActiveDescendantID = descendant;
    this.setState({
      focusedOptionIndex: index,
    });

    // to force a re-render of the outer component to change the aria props
    this.props.store.setEditorState(this.props.store.getEditorState());
  };

  commitSelection = () => {
    const mention = this.props.suggestions[this.state.focusedOptionIndex];
    if (!this.props.store.getIsOpened() || !mention) {
      return 'not-handled';
    }

    this.onMentionSelect(mention);
    return 'handled';
  };

  openDropdown = () => {
    // This is a really nasty way of attaching & releasing the key related functions.
    // It assumes that the keyFunctions object will not loose its reference and
    // by this we can replace inner parameters spread over different modules.
    // This better be some registering & unregistering logic. PRs are welcome :)
    this.props.callbacks.handleReturn = this.commitSelection;
    this.props.callbacks.keyBindingFn = (keyboardEvent) => {
      // arrow down
      if (keyboardEvent.keyCode === 40) {
        this.onDownArrow(keyboardEvent);
      }
      // arrow up
      if (keyboardEvent.keyCode === 38) {
        this.onUpArrow(keyboardEvent);
      }
      // escape
      if (keyboardEvent.keyCode === 27) {
        this.onEscape(keyboardEvent);
      }
      // tab
      if (keyboardEvent.keyCode === 9) {
        this.onTab(keyboardEvent);
      }
      return undefined;
    };

    const descendant = `mention-option-${this.key}-${this.state.focusedOptionIndex}`;
    this.props.ariaProps.ariaActiveDescendantID = descendant;
    this.props.ariaProps.ariaOwneeID = `mentions-list-${this.key}`;
    this.props.ariaProps.ariaHasPopup = 'true';
    this.props.ariaProps.ariaExpanded = true;
    this.props.onOpenChange(true);
  };

  closeDropdown = () => {
    // make sure none of these callbacks are triggered
    this.props.callbacks.handleReturn = undefined;
    this.props.callbacks.keyBindingFn = undefined;
    this.props.ariaProps.ariaHasPopup = 'false';
    this.props.ariaProps.ariaExpanded = false;
    this.props.ariaProps.ariaActiveDescendantID = undefined;
    this.props.ariaProps.ariaOwneeID = undefined;
    this.props.onOpenChange(false);
  };

  render() {
    if (!this.props.open) {
      return null;
    }

    const {
      entryComponent,
      popoverComponent,
      popperOptions,
      popoverContainer: PopoverContainer = Popover,
      onOpenChange, // eslint-disable-line
      onAddMention, // eslint-disable-line
      onSearchChange, // eslint-disable-line
      suggestions, // eslint-disable-line
      ariaProps, // eslint-disable-line
      callbacks, // eslint-disable-line
      theme = {},
      store, // eslint-disable-line
      entityMutability, // eslint-disable-line
      positionSuggestions, // eslint-disable-line
      mentionTriggers, // eslint-disable-line
      mentionPrefix, // eslint-disable-line
      groupByKey,
      groupDisplayNameKey,
    } = this.props;

    if (popoverComponent || positionSuggestions) {
      //remove old popover render
      return null;
    }

    let groupedSuggestions =
      groupByKey && !isUndefined(groupByKey) ? groupBy(suggestions, (x) => x[groupByKey]) : null;

    let suggestionElements = [];
    if (groupedSuggestions) {
      let indexCt = 0;
      let groupDisplayName =
        groupDisplayNameKey && !isUndefined(groupDisplayNameKey) ? groupDisplayNameKey : groupByKey;

      forEach(groupedSuggestions, (v, k) => {
        const firstItem = first(v);
        suggestionElements.push(
          <div key={k} className={theme.mentionSuggestionsEntryTitle}>
            <div>{firstItem[groupDisplayName]}</div>
          </div>,
        );
        let thisGroup = v.map((mention, index) => (
          <Entry
            key={has(mention, 'id') ? k + mention.id : k + mention.name}
            onMentionSelect={this.onMentionSelect}
            onMentionFocus={this.onMentionFocus}
            isFocused={this.state.focusedOptionIndex === indexCt + index}
            mention={mention}
            index={indexCt + index}
            id={`mention-option-${k}-${this.key}-${indexCt + index}`}
            theme={theme}
            searchValue={this.lastSearchValue}
            entryComponent={entryComponent || defaultEntryComponent}
          />
        ));
        indexCt += v.length;
        suggestionElements.push(...thisGroup);
      });
    } else {
      suggestionElements = this.props.suggestions.map((mention, index) => (
        <Entry
          key={mention.id != null ? mention.id : mention.name}
          onMentionSelect={this.onMentionSelect}
          onMentionFocus={this.onMentionFocus}
          isFocused={this.state.focusedOptionIndex === index}
          mention={mention}
          index={index}
          id={`mention-option-${this.key}-${index}`}
          theme={theme}
          searchValue={this.lastSearchValue}
          entryComponent={entryComponent || defaultEntryComponent}
        />
      ));
    }

    return (
      <PopoverContainer
        store={this.props.store}
        className={theme.mentionSuggestions}
        popperOptions={popperOptions}
      >
        {suggestionElements}
      </PopoverContainer>
    );
  }
}

export default MentionSuggestions;
